import Select, { createFilter, SelectInstance } from "react-select";
import * as Commons from "oc-commons/Commons";
import { I18N } from "oc-I18N";
import React, { useRef } from "react";

type Option = {
  value: number;
  label: string;
};

type SizeSelectProps = {
  value: number;
  setter: (value: number) => void;
  optionen: Option[];
  dataInputKey: string;
  ariaLabel?: string;
};

export function SizeSelect({ value, setter, optionen, dataInputKey, ariaLabel }: SizeSelectProps) {
  const agt = APP.arrangement;
  const selected = _.minBy(optionen, (o) => Math.abs(o.value - value));
  const normalFilter = createFilter({
    ignoreCase: false,
    ignoreAccents: false,
    trim: true,
    matchFrom: "start",
  });
  const values = optionen.map((o) => o.value);
  const filterCached = Commons.cache((value) => {
    const max = _.min(values.filter((o) => o >= value));
    const min = _.max(values.filter((o) => o < value));
    return [min, max].filter((x) => x);
  });
  const epsilon = 0.001;
  const equals = (a, b) => Math.abs(a - b) < epsilon;
  const filter = (candidate, input) => {
    // @ts-ignore
    const inputNumber = I18N.parseFloat(input, 2);
    if (equals(candidate.value, inputNumber)) {
      return true;
    }
    return (
      normalFilter(candidate, input) ||
      filterCached(inputNumber).some((n) => equals(n, candidate.value))
    );
  };

  const getOptionNearestToValue = (optionen: Option[], stringValue: string) => {
    const value = I18N.parseFloat(stringValue);
    if (isNaN(value)) return;

    const selected = _.minBy(optionen, (o) => Math.abs(o.value - value));
    return selected;
  };

  const selectInputRef = useRef<SelectInstance<Option> | null>(null);

  return (
    <>
      <span data-select-key={dataInputKey} data-value={selected?.value} className={"ml8"}>
        {/* @ts-ignore */}
        <Select
          ref={selectInputRef}
          classNamePrefix="my-select"
          value={selected}
          options={optionen}
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          onChange={(w) => {
            const selected = agt.selectedObjects[0];
            if (selected != null) {
              setter(w?.value);
              APP.dirty();
            }
          }}
          onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
            setTimeout(() => {
              const focusableOptions = selectInputRef.current?.getFocusableOptions();
              const shouldFocusOption = getOptionNearestToValue(
                focusableOptions,
                (ev.target as HTMLInputElement).value
              );
              if (shouldFocusOption != null) {
                selectInputRef.current?.setState({
                  focusedOption: shouldFocusOption,
                });
              }
            }, 10);
          }}
          onBlur={(ev: React.FocusEvent<HTMLInputElement>) => {
            if (ev.target?.value === "") return;
            const selectedOption = getOptionNearestToValue(optionen, ev.target?.value);
            if (selectedOption != null) {
              setter(selectedOption.value);
              APP.dirty();
            }
          }}
          filterOption={filter}
          aria-label={ariaLabel || I18N("aria::Größe auswählen")}
        />
      </span>
    </>
  );
}
